import React, { useRef, useState, useEffect } from "react";

function AudioAnswer({ value, onChange, questionKey, currentText }) {
  const [recording, setRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcriptionError, setTranscriptionError] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunks = useRef([]);
  const timerRef = useRef(null);
  const maxRecordingTime = 60; // 60 Sekunden = 1 Minute

  // Helper: Erkenne unterstütztes Audio-Format
  const getSupportedMimeType = () => {
    const types = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4',
      'audio/ogg;codecs=opus',
      'audio/wav'
    ];

    for (const type of types) {
      if (MediaRecorder.isTypeSupported(type)) {
        return type;
      }
    }

    // Fallback (sollte nie passieren)
    return 'audio/webm';
  };

  const startRecording = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

    // Erkenne unterstütztes Format
    const mimeType = getSupportedMimeType();
    console.log('🎤 Verwende Audio-Format:', mimeType);

    mediaRecorderRef.current = new MediaRecorder(stream, { mimeType });
    chunks.current = [];
    setRecordingTime(0);

    mediaRecorderRef.current.ondataavailable = (e) => chunks.current.push(e.data);
    mediaRecorderRef.current.onstop = async () => {
      // Verwende den tatsächlichen MIME-Type vom MediaRecorder
      const blob = new Blob(chunks.current, { type: mimeType });
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);

      // Transkription starten
      setIsTranscribing(true);
      setTranscriptionError(null);

      try {
        // Transkribiere Audio via Backend
        const transcriptText = await transcribeAudio(blob);

        // Füge Transkript zum bestehenden Text hinzu
        const separator = currentText && currentText.trim() ? " " : "";
        const newText = currentText + separator + transcriptText;

        // Rufe onChange mit dem neuen Text auf
        onChange(newText);

      } catch (error) {
        console.error('Transkription fehlgeschlagen:', error);
        setTranscriptionError(error.message);
      } finally {
        setIsTranscribing(false);
      }

      // Stop all audio tracks
      stream.getTracks().forEach(track => track.stop());

      // Clear timer
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
    
    mediaRecorderRef.current.start();
    setRecording(true);
    
    // Start timer
    timerRef.current = setInterval(() => {
      setRecordingTime(prev => {
        const newTime = prev + 1;
        // Auto-stop at 60 seconds
        if (newTime >= maxRecordingTime) {
          stopRecording();
        }
        return newTime;
      });
    }, 1000);
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecording(false);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);


  async function transcribeAudio(blobOrFile) {
    try {
      // 1. FormData erstellen
      const formData = new FormData();

      // 2. Blob zu File konvertieren (mit korrektem Namen & Extension)
      let file = blobOrFile;
      if (blobOrFile instanceof Blob && !(blobOrFile instanceof File)) {
        // Extrahiere Extension aus MIME-Type
        const mimeType = blobOrFile.type || 'audio/webm';
        let extension = 'webm'; // Default

        if (mimeType.includes('webm')) extension = 'webm';
        else if (mimeType.includes('mp4')) extension = 'mp4';
        else if (mimeType.includes('m4a')) extension = 'm4a';
        else if (mimeType.includes('mpeg') || mimeType.includes('mp3')) extension = 'mp3';
        else if (mimeType.includes('ogg')) extension = 'ogg';
        else if (mimeType.includes('wav')) extension = 'wav';
        else if (mimeType.includes('flac')) extension = 'flac';

        file = new File([blobOrFile], `recording.${extension}`, {
          type: mimeType
        });

        console.log('📝 File erstellt:', {
          name: file.name,
          type: file.type,
          extension: extension
        });
      }

      formData.append('audio', file);

      console.log('📤 Sende Audio zur Transkription:', {
        name: file.name,
        size: file.size,
        type: file.type
      });

      // 3. API-Endpoint bestimmen (lokal vs. production)
      const apiUrl = '/api/transcribe';

      // 4. Request an Backend senden
      const response = await fetch(apiUrl, {
        method: 'POST',
        body: formData,
        // KEIN Content-Type Header - Browser setzt multipart/form-data automatisch
      });

      // 5. Response verarbeiten
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Server-Fehler: ${response.status}`);
      }

      const data = await response.json();

      console.log('✅ Transkription erfolgreich:', {
        textLength: data.text?.length,
        duration: data.metadata?.duration_ms
      });

      return data.text || '';

    } catch (error) {
      console.error('❌ Transkriptions-Fehler:', error);

      // User-freundliche Fehlermeldungen
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Netzwerkfehler. Bitte Internetverbindung prüfen.');
      }

      if (error.message.includes('429')) {
        throw new Error('Zu viele Anfragen. Bitte warte einen Moment.');
      }

      throw new Error(error.message || 'Transkription fehlgeschlagen');
    }
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        {!recording && (
          <button type="button" onClick={startRecording} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 6 
          }}>
            🎤 Audio aufnehmen (max. 1 Min.)
          </button>
        )}
        {recording && (
          <>
            <button 
              type="button" 
              onClick={stopRecording}
              style={{
                background: '#FD2C4E',
                color: 'white',
                animation: 'pulse 1.5s ease-in-out infinite',
                display: 'flex',
                alignItems: 'center',
                gap: 6
              }}
            >
              ■ Stop
            </button>
            <span style={{ 
              fontSize: '1.1rem', 
              fontWeight: 600,
              color: recordingTime >= 50 ? '#FD2C4E' : '#333'
            }}>
              {formatTime(recordingTime)} / 1:00
            </span>
          </>
        )}
        {audioUrl && (
          <audio controls src={audioUrl} style={{ maxWidth: "300px", height: "40px" }} />
        )}
      </div>

      {isTranscribing && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 12px',
          background: '#f0f9ff',
          borderRadius: 6,
          border: '1px solid #0ea5e9',
          marginTop: 8
        }}>
          <span style={{
            display: 'inline-block',
            width: 16,
            height: 16,
            border: '2px solid #0ea5e9',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <span style={{ fontSize: '0.9rem', color: '#0369a1' }}>
            Transkribiere Audio...
          </span>
        </div>
      )}

      {transcriptionError && (
        <div style={{
          padding: '8px 12px',
          background: '#fef2f2',
          borderRadius: 6,
          border: '1px solid #ef4444',
          marginTop: 8
        }}>
          <span style={{ fontSize: '0.9rem', color: '#dc2626' }}>
            ⚠️ {transcriptionError}
          </span>
        </div>
      )}

      <p style={{
        fontSize: '0.85rem',
        color: '#666',
        marginTop: 8,
        marginBottom: 0,
        fontStyle: 'italic'
      }}>
        💡 Tipp: Der Audiotext wird automatisch in die Textarea eingefügt und kann bearbeitet werden
      </p>

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}

export default AudioAnswer;